import * as Sentry from "@sentry/tanstackstart-react";
import { isNotFound, isRedirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { getTenantParams } from "#/modules/util.fetch-client/tenant-context.server";
import { resolveClientIp } from "#/modules/util.instrumentation/client-ip.server";

/**
 * One GlitchTip log line per server-function call: which function ran, for which school, how long
 * it took and how it ended. Together with the backend's per-request and per-command lines (same
 * trace id) this gives a timeline of what happened, also when nothing threw. Failures are logged
 * at warn only; the Sentry middleware ahead of this one already captures the exception itself.
 */
export const serverFnLogMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next, method, serverFnMeta }) => {
    const startedAt = performance.now();
    const attributes = () => ({
      "server_fn.name": serverFnMeta.name,
      "server_fn.file": serverFnMeta.filename,
      "http.method": method,
      "school.slug": getTenantParams()?.school ?? "",
      "client.address": resolveClientIp() ?? "",
      "user_agent.original": getRequest()?.headers.get("user-agent") ?? "",
      duration_ms: Math.round(performance.now() - startedAt),
    });

    try {
      const result = await next();
      Sentry.logger.info(`server fn ${serverFnMeta.name} ok`, attributes());
      return result;
    } catch (cause) {
      if (isRedirect(cause)) {
        Sentry.logger.info(`server fn ${serverFnMeta.name} redirect`, attributes());
      } else if (isNotFound(cause)) {
        Sentry.logger.info(`server fn ${serverFnMeta.name} not found`, attributes());
      } else {
        Sentry.logger.warn(`server fn ${serverFnMeta.name} failed`, {
          ...attributes(),
          "error.message": cause instanceof Error ? cause.message : String(cause),
        });
      }
      throw cause;
    }
  },
);
